import { Box, Grid, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import useClinic from "../../../hooks/useClinic";
import ActionableSearchBar from "../../../components/UI/ActionableSearchBar";
import ListHeader from "../../../components/UI/ListHeader";
import {
  deleteExternalExam,
  getAllExternalExams,
  getProgressAnalysisStatusForExternalExam,
} from "../../../services/Exam";
import { itemsRendering, pagesCount } from "../../../utils/paginationHelper";
import PaginationWrapper from "../../../components/UI/PaginationWrapper";
import Loader from "../../../components/UI/Loader";
import { ToastContext } from "../../../contexts/ToastContext";
import SnackbarMessage from "../../../components/UI/SnackbarMessage";
import ExternalExamCard from "../../../components/UI/ExternalExamCard";
import ExternalExamDetailsModal from "../../../components/UI/ExternalExamDetailsModal";
import DeleteExternalExamModal from "../../../components/UI/DeleteExternalExamModal";
import { hideLoader, showLoader } from "../../../components/loader/loader";

const ExternalExamsListContainer = ({
  patientId,
  maxNumOfRows = 4,
  patientName,
}) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const { clinicId } = useClinic();
  const { toast, setToast } = useContext(ToastContext);
  const [externalExamsList, setExternalExamsList] = useState([]);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [examDetails, setExamDetails] = useState(null);
  const [progressionAnalysisFound, setProgressionAnalysisFound] =
    useState(false);

  const [order, setOrder] = useState("desc");
  const [orderByOptions, setOrderByOptions] = useState(["completionDate"]);

  const headCells = [
    {
      id: "empty",
      label: null,
      gridSize: 0,
      orderUsing: ["eye"],
    },
    {
      id: "eye",
      label: t("exams_column_eye"),
      gridSize: 1.5,
      orderUsing: ["eye"],
    },
    {
      id: "algorithmTypeReadable",
      label: t("type"),
      gridSize: 6,
      orderUsing: ["algorithmTypeReadable"],
    },
    {
      id: "completionDate",
      label: t("Completion Date"),
      gridSize: 3.5,
      orderUsing: ["completionDate"],
    },
  ];

  const extraCells = [
    {
      id: "manage",
      label: "",
      gridSize: 1,
    },
  ];

  // const pageCoun
  const onFilterChange = useCallback(
    (event) => {
      if (page > 1) setPage(1);
      setSearchTerm(event.target.value);
    },
    [page, setPage]
  );

  // Sorting logic
  const handleRequestSort = (event, property) => {
    const isAsc =
      orderByOptions.length &&
      orderByOptions[0] === property[0] &&
      order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderByOptions(property);
  };

  // Sorting rows
  const sortedRows = useMemo(() => {
    return externalExamsList.slice().sort((a, b) => {
      if (order === "asc") {
        return a[orderByOptions[0]] > b[orderByOptions[0]] ? 1 : -1;
      } else {
        return a[orderByOptions[0]] < b[orderByOptions[0]] ? 1 : -1;
      }
    });
  }, [externalExamsList, order, orderByOptions]);

  // Filtering rows
  const filteredRows = useMemo(
    () =>
      sortedRows.filter((externalExam) => {
        return !(
          searchTerm?.length &&
          !externalExam?.eye
            ?.toLowerCase()
            ?.includes(searchTerm?.toLowerCase()) &&
          !externalExam?.algorithmTypeReadable
            ?.toLowerCase()
            ?.includes(searchTerm?.toLowerCase()) &&
          !externalExam?.completionDate
            ?.toLowerCase()
            ?.includes(searchTerm?.toLowerCase())
        );
      }),
    [sortedRows, searchTerm]
  );

  const rowsToRender = useMemo(
    () => itemsRendering(filteredRows, page, Math.max(maxNumOfRows, 4)),
    [filteredRows, maxNumOfRows, page]
  );

  const handleCheckboxClick = useCallback(
    (id) => {
      const selectedIndex = selected.indexOf(id);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
      }
      setSelected(newSelected);
    },
    [selected]
  );

  const isSelected = useCallback(
    (id) => selected.indexOf(id) !== -1,
    [selected]
  );

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelecteds = rowsToRender.map((n) => n.id);
        setSelected(newSelecteds);
        return;
      }
      setSelected([]);
    },
    [rowsToRender]
  );

  const DeleteExternalExam = useCallback(async () => {
    setDeleteModal(false);

    const generateDeletePromise = async (id) => {
      // get token
      const token = await getAccessTokenSilently();
      await deleteExternalExam(token, clinicId, patientId, id);
    };

    // delete from api
    try {
      showLoader();
      const deletePromises = selected.map((id) => generateDeletePromise(id));

      await Promise.all(deletePromises);
      hideLoader();
      setToast(() => ({
        success: true,
        message: t("external_exam_delete_success"),
      }));
    } catch (error) {
      hideLoader();
      setToast(() => ({
        success: false,
        message: t("external_delete_error"),
      }));
    }

    const newRows = externalExamsList.filter(
      (externalExam) => !selected.includes(externalExam?.id)
    );
    setExternalExamsList([...newRows]);

    setSelected([]);
  }, [
    clinicId,
    getAccessTokenSilently,
    patientId,
    externalExamsList,
    selected,
    setToast,
    t,
  ]);

  const handleViewReport = (data) => {
    setExamDetails(data);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
  };

  const onPaginationChange = useCallback((_, value) => {
    setPage(value);
  }, []);

  const clearSearchFilter = useCallback((_) => {
    setSearchTerm("");
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const fetch = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await getAllExternalExams(token, clinicId, patientId);
        setExternalExamsList(res);
        setIsLoading(false);
      } catch (e) {}
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetProgressionAnalysis = useCallback(
    async (selectedId) => {
      const token = await getAccessTokenSilently();
      const analysisStatus = await getProgressAnalysisStatusForExternalExam(
        token,
        clinicId,
        patientId,
        selectedId
      );

      if (Array.isArray(analysisStatus?.data)) {
        if (analysisStatus?.data?.length === 0) {
          setProgressionAnalysisFound(false);
        } else if (analysisStatus?.data?.length > 0) {
          setProgressionAnalysisFound(true);
        }
      }
    },
    [getAccessTokenSilently, clinicId, patientId]
  );

  const handleGetProgressionAnalysisMultiple = useCallback(async () => {
    if (Array.isArray(selected)) {
      if (selected?.length >= 10) {
        setProgressionAnalysisFound(true);
        return; // Stop further API calls if array is too long
      }
      for (const item of selected) {
        const analysisFound = await handleGetProgressionAnalysis(item);

        if (analysisFound) {
          break; // Stop further API calls if analysis is found
        }
      }
    }
  }, [selected, handleGetProgressionAnalysis]);

  const handleDeleteClick = useCallback(async () => {
    showLoader();
    await handleGetProgressionAnalysisMultiple();
    hideLoader();

    setDeleteModal(true);
  }, [handleGetProgressionAnalysisMultiple]);

  const handleDeleteClose = useCallback((event, reason) => {
    if (reason !== "backdropClick") {
      setDeleteModal(false);
    }
  }, []);

  return (
    <>
      <Grid
        item
        display={"flex"}
        alignItems={"center"}
        xs={12}
        justifyContent={"right"}
        gap={2}
        mb={{ xs: 1, sm: 0 }}
      >
        <ActionableSearchBar
          placeholder={`${t("word_search")}...`}
          onSearchChange={onFilterChange}
          onClear={clearSearchFilter}
          showDeleteButton={selected?.length > 0}
          disableDeleteButton={!selected?.length}
          deleteButtonOnClick={handleDeleteClick}
          showAddButton={false}
          addButtonOnClick={() => {}}
          selectedRows={selected}
          addTooltip={t("word_add_external_exam")}
          searchTooltip={t("word_search_external_exams")}
        />
      </Grid>
      {isLoading ? (
        <Loader containerHeight="30vh" />
      ) : (
        <Grid container mt={{ xs: 0, sm: 3 }}>
          <Grid item xs={12}>
            <ListHeader
              containerPaddingX={0}
              headCells={headCells}
              numSelected={selected.length}
              order={order}
              orderBy={orderByOptions?.[0] ?? "completionDate"}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rowsToRender.length}
              checkBoxGrid={1}
              shouldDisplayGrid="sm"
              displayFirtsBeforeCheckbox
              extraCells={extraCells}
            />
            {rowsToRender.length > 0 ? (
              <Box mt={1}>
                {rowsToRender.map((row) => {
                  const isItemSelected = isSelected(row?.id);
                  return (
                    <ExternalExamCard
                      key={row.id}
                      row={row}
                      patientId={patientId}
                      isSelected={isItemSelected}
                      headCells={headCells}
                      handleCheckboxClick={handleCheckboxClick}
                      handleViewReportClick={() => handleViewReport(row)}
                    />
                  );
                })}
              </Box>
            ) : (
              <Box
                my={12}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
                gap="20px"
              >
                <Typography fontWeight={600} fontSize="20px">
                  {`${patientName} ${t("has_no_external_exams")}`}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      )}
      {externalExamsList?.length > rowsToRender?.length && (
        <PaginationWrapper
          page={page}
          count={pagesCount(externalExamsList, Math.max(maxNumOfRows, 4))}
          onChange={onPaginationChange}
        />
      )}

      <DeleteExternalExamModal
        open={deleteModal}
        onClose={handleDeleteClose}
        onConfirm={DeleteExternalExam}
        onCancel={handleDeleteClose}
        progressionAnalysisFound={progressionAnalysisFound}
        progressionAnalysisFoundCount={selected.length}
      />

      <SnackbarMessage
        open={Boolean(toast.message)}
        onClose={() => setToast({ success: false, message: null })}
        success={toast.success}
        autoHideDuration={8000}
        text={toast.message}
      />
      <ExternalExamDetailsModal
        openModal={showDetailsModal}
        handleClose={handleCloseDetailsModal}
        examDetails={examDetails}
      />
    </>
  );
};

export default ExternalExamsListContainer;
